import { callable } from '../callable';

export interface CreateAdmissionRequest {
  tenantId: string;
  // Mode A: link to existing person
  personId?: string;
  // Mode B: create/find person inline
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  programId: string;
  termId: string;
}

export interface SubmitAdmissionDocumentsRequest {
  tenantId: string;
  admissionId: string;
  documents: Array<{ type: string; url: string; approved?: boolean }>;
}

export interface StartReviewRequest {
  tenantId: string;
  admissionId: string;
}

export interface SubmitVoteRequest {
  tenantId: string;
  admissionId: string;
  verdict: 'approve' | 'reject' | 'clarify';
  notes: string;
}

export interface SubmitVoteResponse {
  /** `approved_pending`: the committee approved, but a rule (documents, the
   *  ladder entry rule) blocked the automatic acceptance — for the registrar. */
  quorumResult: 'accepted' | 'approved_pending' | 'rejected' | 'pending';
}

export interface PublicSubmitAdmissionRequest {
  tenantId: string;
  programId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  church?: string;
  /** Free-form answers from the program's FormTemplate, keyed by field.id.
   *  Also contains metadata like _templateId and submittedVia. */
  applicantFormData?: Record<string, string>;
  /** Documents the applicant uploaded against the program's requirements. */
  documents?: Array<{ type: string; storagePath: string }>;
}

export interface PublicSubmitAdmissionResponse {
  admissionId: string;
  personId: string;
}

export interface ChangeProgramWithEnrollmentTransitionRequest {
  tenantId: string;
  admissionId: string;
  newProgramId: string;
  enrollmentTransition: {
    action: 'keep' | 'drop_all' | 'drop_selected';
    selectedEnrollmentIds?: string[];
  };
  sectionTransition: {
    action: 'none' | 'add_compatible' | 'add_selected';
    selectedSectionIds?: string[];
  };
  tuitionPlanDecision: 'keep' | 'recreate';
  notifyStudent?: boolean;
}

export interface ChangeProgramWithEnrollmentTransitionResponse {
  admissionId: string;
  previousProgramId: string;
  newProgramId: string;
  enrollmentsDropped: number;
  enrollmentsAdded: number;
  tuitionPlanDecision: 'kept' | 'recreated';
  newTuitionPlanId?: string;
  auditEventId: string;
}

export const admissionsApi = {
  create: async (data: CreateAdmissionRequest) => {
    const createAdmission = callable<CreateAdmissionRequest, { id: string; personId: string }>(
      'createAdmission',
    );
    const result = await createAdmission(data);
    return result.data;
  },

  submitDocuments: async (data: SubmitAdmissionDocumentsRequest) => {
    const submitDocs = callable<SubmitAdmissionDocumentsRequest, { id: string }>(
      'submitAdmissionDocuments',
    );
    const result = await submitDocs(data);
    return result.data;
  },

  startReview: async (data: StartReviewRequest) => {
    const fn = callable<StartReviewRequest, void>('startAdmissionReview');
    await fn(data);
  },

  submitVote: async (data: SubmitVoteRequest): Promise<SubmitVoteResponse> => {
    const fn = callable<SubmitVoteRequest, SubmitVoteResponse>('submitReviewVote');
    const result = await fn(data);
    return result.data;
  },

  updateVote: async (data: SubmitVoteRequest): Promise<SubmitVoteResponse> => {
    const fn = callable<SubmitVoteRequest, SubmitVoteResponse>('updateReviewVote');
    const result = await fn(data);
    return result.data;
  },

  delete: async (data: { tenantId: string; admissionId: string }): Promise<void> => {
    const fn = callable<{ tenantId: string; admissionId: string }, { success: boolean }>(
      'deleteAdmission',
    );
    await fn(data);
  },

  publicSubmit: async (
    data: PublicSubmitAdmissionRequest,
  ): Promise<PublicSubmitAdmissionResponse> => {
    const fn = callable<PublicSubmitAdmissionRequest, PublicSubmitAdmissionResponse>(
      'publicSubmitAdmission',
    );
    const result = await fn(data);
    return result.data;
  },

  changeProgramWithEnrollmentTransition: async (
    data: ChangeProgramWithEnrollmentTransitionRequest,
  ): Promise<ChangeProgramWithEnrollmentTransitionResponse> => {
    const fn = callable<
      ChangeProgramWithEnrollmentTransitionRequest,
      ChangeProgramWithEnrollmentTransitionResponse
    >('changeProgramWithEnrollmentTransition');
    const result = await fn(data);
    return result.data;
  },

  withdraw: async (data: {
    tenantId: string;
    admissionId: string;
    withdrawalType?: string;
    withdrawalReason: string;
    keepAccess?: boolean;
    /** Per-installment retained amount overrides keyed by installment id. */
    installmentRetentions?: Record<string, number>;
  }): Promise<WithdrawResult> => {
    const fn = callable<typeof data, WithdrawResult>('withdrawStudent');
    const result = await fn(data);
    return result.data;
  },

  /**
   * Dry-run preview: compute financial impact (retained / refund / zeroed
   * installments + per-installment breakdown) without persisting any changes.
   * Accepts the same `installmentRetentions` overrides so the dialog can show
   * live updates as the admin edits per-cuota amounts.
   */
  withdrawPreview: async (data: {
    tenantId: string;
    admissionId: string;
    keepAccess?: boolean;
    installmentRetentions?: Record<string, number>;
  }): Promise<WithdrawResult> => {
    const fn = callable<typeof data, WithdrawResult>('withdrawStudentPreview');
    const result = await fn(data);
    return result.data;
  },
};

export interface WithdrawalInstallmentImpactDto {
  planId: string;
  installmentId: string;
  label: string;
  dueDate: string;
  type: 'enrollment' | 'monthly';
  originalAmount: number;
  paidAmount: number;
  retainedAmount: number;
  refundAmount: number;
  /** The plan's currency. */
  currency: string;
}

interface WithdrawResult {
  success?: boolean;
  admissionId?: string;
  personId?: string;
  tuitionPlansWithdrawn: number;
  enrollmentsDropped: number;
  /** True when the program enrollment went to `withdrawn` too. */
  programEnrollmentWithdrawn?: boolean;
  accessRevoked: boolean;
  retainedAmount: number;
  refundAmount: number;
  zeroedInstallmentsCount: number;
  installmentImpacts: WithdrawalInstallmentImpactDto[];
  /** The plans' currency; null without plans. */
  currency?: string | null;
}
